import {Component, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {NgxSpinnerService} from 'ngx-spinner';
import {Auth} from '../providers/auth';
import {Api} from '../providers/api';
import {Plan, PaymentProvider} from '../providers/models';
import {
  taglineFor as taglineForPlan,
  featuresFor as featuresForPlan,
  priceDisplay as priceDisplayFor,
  annualTotal as annualTotalFor,
  describeFromPayload
} from '../providers/plan-display';
import {loadPayPalSDK, renderPayPalButton} from '../shared/paypal-sdk';

@Component({
  selector: 'app-pago',
  templateUrl: './pago.component.html',
  styleUrls: ['./pago.component.css']
})
export class PagoComponent implements OnInit {

  availablePlans: Plan[] = [];
  selectedPlan: Plan|null = null;
  billingCycle: 'mensual' | 'anual' = 'mensual';
  step: 'select' | 'confirm' = 'select';
  paypalClientId = '';
  amount = '';
  currency = 'USD';
  loadingPlans = true;
  plansLoadError: string|null = null;
  paySuccess = false;
  errorMsg: string = null;
  isLoadingPayPal = false;
  private guid: string = null;

  // Payment-agnostic provider selector (PayPal adapter first)
  availableProviders: PaymentProvider[] = [];
  selectedProvider: PaymentProvider | null = null;

  // Subscription status for renewal/switch UI
  subscriptionExpiresAt: string | null = null;
  accountStatus: string | null = null;
  daysUntilExpiry: number | null = null;
  billingCycleCurrent: string | null = null;
  paymentProviderCurrent: string | null = null;

  // Same listing rule as the wizard: only online-signup plans for the
  // selected billing cycle (franquicia never arrives from C1 anyway).
  // Q4-DEFAULT (T9 DoD): no id-based exclusion — every plan from C1
  // is listed; re-add an exclusion only if the backend keeps a legacy id.
  get filteredPlans(): Plan[] {
    return this.availablePlans.filter(function(p) {
      var plan: any = p;
      if (plan.selfServe === false || plan.tier === 'franquicia') { return false; }
      return (plan.billing || plan.billingCycle || 'mensual') === this.billingCycle;
    }.bind(this));
  }

  // Display helpers (same source as the wizard: backend C1 display copy
  // primary, tier copy fallback). Display-only: charging always uses the
  // raw plan total via amount/totalAmount.
  taglineForPlan(plan: Plan): string {
    return taglineForPlan(plan, describeFromPayload(plan));
  }

  featuresForPlan(plan: Plan): { text: string; included: boolean }[] {
    var shared = featuresForPlan(plan);
    return shared || [];
  }

  priceDisplay(plan: Plan): string {
    return priceDisplayFor(plan);
  }

  annualTotal(plan: Plan): string {
    return annualTotalFor(plan);
  }

  // Q2-DEFAULT (T9 DoD): prominent per-month derived math removed; the
  // payload amount is the single source of truth for prices.

  get taxAmount(): string {
    if (!this.selectedPlan) return '0.00';
    var p: any = this.selectedPlan;
    if (p.taxAmount) return Number(p.taxAmount).toFixed(2);
    if (p.taxRate) { var amt = Number(p.amount || 0); var t = amt * Number(p.taxRate) / 100; return t.toFixed(2); }
    if (p.tax) return Number(p.tax).toFixed(2);
    return '0.00';
  }

  get totalAmount(): string {
    if (!this.selectedPlan) return '0.00';
    var amt = Number(this.selectedPlan.amount || 0);
    var tax = Number(this.taxAmount || 0);
    return (amt + tax).toFixed(2);
  }

  get hasTax(): boolean { return Number(this.taxAmount) > 0; }

  get canRenderPayPal(): boolean {
    return !!this.selectedPlan && !!this.selectedProvider && this.selectedProvider.id === 'paypal' && !!this.selectedProvider.clientId;
  }

  get isNonPayPalProvider(): boolean {
    return !!this.selectedProvider && this.selectedProvider.id !== 'paypal';
  }

  get pagoTitle(): string {
    if (this.accountStatus === 'EXPIRED') return 'Reactivar suscripción';
    return 'Activar suscripción';
  }

  goToConfirm(): void {
    if (!this.selectedPlan || this.isLoadingPayPal) return;
    if (this.isNonPayPalProvider) {
      this.step = 'confirm';
      this.isLoadingPayPal = false;
      return;
    }
    if (!this.canRenderPayPal) {
      this.errorMsg = 'El pago no está configurado. Intente nuevamente más tarde.';
      return;
    }
    this.step = 'confirm';
    this.isLoadingPayPal = true;
    setTimeout(function() { this.initPayment(); }.bind(this), 150);
  }

  backToSelect(): void {
    this.step = 'select';
    this.isLoadingPayPal = false;
    var c = document.getElementById('paypal-button-container');
    if (c) c.innerHTML = '';
  }

  switchBillingCycle(cycle: 'mensual' | 'anual'): void {
    this.billingCycle = cycle;
    this.step = 'select';
    this.isLoadingPayPal = false;
    var c0 = document.getElementById('paypal-button-container');
    if (c0) { c0.innerHTML = ''; }
    var stillVisible = false;
    for (var i = 0; i < this.filteredPlans.length; i++) {
      if (this.selectedPlan && this.filteredPlans[i].id === this.selectedPlan.id) {
        stillVisible = true;
        break;
      }
    }
    if (!stillVisible && this.filteredPlans.length) {
      this.selectPlan(this.filteredPlans[0]);
    } else if (!stillVisible) {
      this.selectedPlan = null;
      this.amount = '';
      this.currency = 'USD';
      var container = document.getElementById('paypal-button-container');
      if (container) { container.innerHTML = ''; }
    }
  }

  constructor(private router: Router,
              private spinner: NgxSpinnerService,
              private auth: Auth,
              private api: Api) {
  }

  ngOnInit() {
    this.guid = this.auth.user && this.auth.user.circuito && this.auth.user.circuito[0]
      ? this.auth.user.circuito[0].guid : null;
    if (!this.guid) {
      this.errorMsg = 'No se encontró su circuito. Contacte a soporte para activar su cuenta.';
      return;
    }
    this.loadPlans();
    this.loadSubscriptionStatus();
  }

  // Q7 preload stash (contratos-congelados T8, R8): status.planId is stored
  // when the status response arrives and applied once plans are available
  // (stash-then-apply: either response may arrive first).
  private statusPlanId: string | null = null;

  private loadPlans(): void {
    this.loadingPlans = true;
    this.plansLoadError = null;
    this.errorMsg = null;
    this.step = 'select';
    this.api.getPlans().subscribe(function(res: any) {
      var data = res && res.data ? res.data : res;
      var plans = data.plans || [];
      var paypal = data.paypal || {};
      var providers = data.providers || null;
      this.availablePlans = Array.isArray(plans) ? plans : [];
      // Providers from GET /plans compat (providers field) or fallback to paypal config
      // Only PayPal (+ card via PayPal SDK) — filter out other providers
      var filteredFromPlans = Array.isArray(providers) ? providers.filter(function(p: any) { return p && p.id === 'paypal'; }) : [];
      if (filteredFromPlans.length) {
        this.availableProviders = filteredFromPlans;
      } else if (paypal && paypal.clientId) {
        this.availableProviders = [{id: 'paypal', name: 'PayPal', clientId: paypal.clientId, currency: paypal.currency || 'USD'}];
      }
      // Default selected provider = paypal
      if (!this.selectedProvider && this.availableProviders.length) {
        var found = null;
        for (var i = 0; i < this.availableProviders.length; i++) {
          if (this.availableProviders[i].id === 'paypal') { found = this.availableProviders[i]; break; }
        }
        this.selectedProvider = found || this.availableProviders[0];
        if (this.selectedProvider) {
          this.paypalClientId = this.selectedProvider.clientId || paypal.clientId || '';
          this.currency = this.selectedProvider.currency || paypal.currency || 'USD';
        }
      } else if (paypal.clientId) {
        this.paypalClientId = paypal.clientId || '';
      }
      this.applyPlanPreselection();
      this.loadingPlans = false;
      // Also fetch authoritative provider catalog
      this.loadPaymentProviders();
    }.bind(this), function() {
      this.loadingPlans = false;
      this.plansLoadError = 'No se pudieron cargar los planes. Intente nuevamente.';
    }.bind(this));
  }

  private loadPaymentProviders(): void {
    this.api.getPaymentProviders().subscribe(function(res: any) {
      var data = res && res.data ? res.data : res;
      var providers = data.providers || data;
      if (!Array.isArray(providers)) { providers = []; }
      // Filter to PayPal only — card (debit/credit) is handled as funding source inside PayPal SDK
      providers = providers.filter(function(p: any) { return p && p.id === 'paypal'; });
      if (providers.length) {
        this.availableProviders = providers;
        var found = null;
        for (var i = 0; i < providers.length; i++) {
          if (providers[i].id === 'paypal') { found = providers[i]; break; }
        }
        // Keep existing selection if already set, otherwise default to paypal
        if (!this.selectedProvider) {
          this.selectedProvider = found || providers[0];
        } else {
          // Refresh reference to keep clientId/currency updated
          for (var j = 0; j < providers.length; j++) {
            if (this.selectedProvider && providers[j].id === this.selectedProvider.id) { this.selectedProvider = providers[j]; break; }
          }
          // If previous selection disappeared, fallback
          var stillExists = false;
          for (var k = 0; k < providers.length; k++) { if (this.selectedProvider && providers[k].id === this.selectedProvider.id) { stillExists = true; break; } }
          if (!stillExists) { this.selectedProvider = found || providers[0]; }
        }
        if (this.selectedProvider) {
          this.paypalClientId = this.selectedProvider.clientId || this.paypalClientId || '';
          if (this.selectedProvider.currency) { this.currency = this.selectedProvider.currency; }
        }
      }
    }.bind(this), function() {
      // silent fallback already handled via getPlans
    }.bind(this));
  }

  private loadSubscriptionStatus(): void {
    if (!this.guid) { return; }
    this.api.getTrialStatus(this.guid).subscribe(function(res: any) {
      var data = res && res.data ? res.data : res;
      this.accountStatus = data.accountStatus || data.subscriptionStatus || null;
      this.subscriptionExpiresAt = data.subscriptionExpiresAt || null;
      this.billingCycleCurrent = data.billingCycle || null;
      this.paymentProviderCurrent = data.paymentProvider || null;
      if (data.daysUntilExpiry !== undefined && data.daysUntilExpiry !== null) {
        this.daysUntilExpiry = Number(data.daysUntilExpiry);
      } else if (this.subscriptionExpiresAt) {
        var diff = new Date(this.subscriptionExpiresAt).getTime() - Date.now();
        this.daysUntilExpiry = Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
      }
      var incomingPlanId = data.planId
        || (data.entitlement && data.entitlement.plan && (data.entitlement.plan.planId || data.entitlement.plan.id))
        || null;
      this.statusPlanId = incomingPlanId ? String(incomingPlanId) : null;
      this.applyPlanPreselection();
    }.bind(this), function() {
      // ignore
    }.bind(this));
  }

  // Q7 lookup order (contratos-congelados T8, R8): status.planId match
  // (planId, then display id) wins; missing planId falls back to
  // filteredPlans[0] during the deploy window; an empty list clears the
  // selection so the template shows the contacto CTA.
  private applyPlanPreselection(): void {
    var plans = this.filteredPlans;
    if (!plans.length) {
      this.selectedPlan = null;
      this.amount = '';
      return;
    }
    var match: Plan | null = null;
    if (this.statusPlanId) {
      for (var i = 0; i < plans.length; i++) {
        var candidate: any = plans[i];
        if (candidate.planId === this.statusPlanId || candidate.id === this.statusPlanId) {
          match = plans[i];
          break;
        }
      }
    }
    this.selectPlan(match || plans[0]);
  }

  selectPlan(plan: Plan): void {
    this.selectedPlan = plan;
    this.amount = plan.amount;
    this.currency = plan.currency || (this.selectedProvider && this.selectedProvider.currency) || 'USD';
    this.errorMsg = null;
  }

  selectProvider(provider: PaymentProvider): void {
    this.selectedProvider = provider;
    if (provider.currency) { this.currency = provider.currency; }
    if (provider.clientId) { this.paypalClientId = provider.clientId; }
    this.errorMsg = null;
    // Reset confirm step if switching provider
    if (this.step === 'confirm') {
      var c = document.getElementById('paypal-button-container');
      if (c) c.innerHTML = '';
      this.step = 'select';
    }
  }

  private initPayment(): void {
    this.errorMsg = null;
    if (!this.selectedPlan) { this.isLoadingPayPal = false; return; }
    if (this.isNonPayPalProvider) {
      // Agnostic placeholder for future providers — no PayPal SDK
      this.isLoadingPayPal = false;
      return;
    }
    this.isLoadingPayPal = true;
    if (!this.selectedProvider || !this.selectedProvider.clientId || this.selectedProvider.id !== 'paypal') {
      this.errorMsg = 'El pago no está configurado. Intente nuevamente más tarde.';
      this.isLoadingPayPal = false;
      return;
    }
    var clientId = this.selectedProvider.clientId;
    var currency = this.selectedProvider.currency || this.currency || 'USD';
    loadPayPalSDK(clientId, currency).then(function(loaded: boolean) {
      if (!loaded) {
        this.errorMsg = 'No se pudo cargar el medio de pago. Intente nuevamente.';
        this.isLoadingPayPal = false;
        return;
      }
      var amountVal = this.hasTax ? this.totalAmount : this.amount;
      renderPayPalButton('paypal-button-container', {
        amount: amountVal,
        currency: currency,
        planAmount: this.amount,
        taxAmount: this.taxAmount,
        onClick: function() {
          this.isLoadingPayPal = true;
          setTimeout(function() { this.isLoadingPayPal = false; }.bind(this), 1500);
        }.bind(this),
        onApprove: function(orderId: string) { this.isLoadingPayPal = false; this.onApprove(orderId); }.bind(this),
        onError: function() { this.onPayError(); }.bind(this),
        onCancel: function() { this.onPayCancel(); }.bind(this)
      });
      this.isLoadingPayPal = false;
    }.bind(this));
  }

  // compat alias
  private initPayPal(): void { this.initPayment(); }

  private onApprove(orderId: string) {
    this.isLoadingPayPal = false;
    this.spinner.show();
    var chosen: any = this.selectedPlan;
    var planId = chosen ? (chosen.planId || chosen.id) : null;
    var providerId = this.selectedProvider ? this.selectedProvider.id : 'paypal';
    this.api.paySubscription(this.guid, orderId, providerId, planId).subscribe(function(res: any) {
      this.spinner.hide();
      this.paySuccess = true;
      try { if ((this.auth as any).subscriptionChanged) { (this.auth as any).subscriptionChanged.next(); } } catch(e) {}
    }.bind(this), function(err) {
      this.spinner.hide();
      this.onPayError(err);
    }.bind(this));
  }

  private onPayError(err?: any) {
    this.isLoadingPayPal = false;
    this.errorMsg = err && err.error && err.error.message
      ? err.error.message
      : 'Error al procesar el pago. Intente nuevamente.';
  }

  private onPayCancel() {
    this.isLoadingPayPal = false;
    this.errorMsg = 'Pago cancelado. Puede volver a intentarlo cuando quiera.';
  }

  retry() {
    this.errorMsg = null;
    if (this.plansLoadError || this.availablePlans.length === 0) {
      this.loadPlans();
      return;
    }
    if (this.step !== 'confirm' || !this.selectedPlan) {
      if (this.selectedPlan) {
        this.step = 'confirm';
      } else {
        return;
      }
    }
    var c = document.getElementById('paypal-button-container');
    if (c) c.innerHTML = '';
    this.isLoadingPayPal = true;
    setTimeout(function() { this.initPayment(); }.bind(this), 150);
  }

  goHome() {
    this.router.navigate(['home']);
  }
}



// WEBPACK FOOTER //
// ./src/app/pago/pago.component.ts